import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from "@/store/useAuthStore.jsx";
import authApi from '@services/authApi.jsx';

const useAuth = () => {
    const {
        syncAuthState,
        tokens
    } = useAuthStore();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // 사용자 정보를 저장할 상태 추가
    const [currentUserData, setCurrentUserData] = useState(null);

    // 토큰 가져오기 유틸리티 함수
    const getAuthTokens = useCallback(() => {
        // 먼저 Zustand 스토어에서 토큰 확인
        let accessToken = tokens.accessToken;

        // 스토어에 없으면 로컬 스토리지에서 확인
        if (!accessToken) accessToken = localStorage.getItem('ab_at');

        return { accessToken };
    }, [tokens]);

    // 사용자 조회
    const getCurrentUser = useCallback(async () => {
        try {
            setLoading(true);
            const isLoggedIn = syncAuthState();

            if (isLoggedIn) {
                try {
                    const userData = await authApi.getCurrentUser();
                    // 사용자 정보를 상태에 저장
                    setCurrentUserData(userData || {});
                    return userData;
                } catch (e) {
                    // 오류 처리 (토큰은 있지만 API 호출 실패)
                    console.error('사용자 정보 가져오기 실패:', e);
                    return {};
                }
            }
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, [syncAuthState]);

    // 초기화 시 로컬 스토리지의 토큰과 상태 동기화
    useEffect(() => {
        getCurrentUser();
    }, [syncAuthState, getCurrentUser]);

    // 현재 사용자 정보를 반환하는 함수 추가
    const user = useCallback(() => {
        return currentUserData || {};
    }, [currentUserData]);

    // 승인 대기 사용자 추가 정보 제출 함수
    const submitPendingApprovalInfo = useCallback(async (additionalInfo) => {
        try {
            setLoading(true);
            setError(null);

            // 토큰 정보 포함하여 요청
            const { refreshToken } = getAuthTokens();
            const response = await authApi.submitPendingApprovalInfo(additionalInfo, refreshToken);

            return response;
        } catch (e) {
            setError(e.response?.data?.message || e.message);
            throw e;
        } finally {
            setLoading(false);
        }
    }, [getAuthTokens]);

    // JWT 디코딩 함수 추가
    const decodeJWT = (token) => {
        if (!token) return null;

        try {
            // JWT는 header.payload.signature 형식
            const base64Payload = token.split('.')[1];
            // Base64 디코딩 (URL-safe base64 처리)
            const payload = atob(base64Payload.replace(/-/g, '+').replace(/_/g, '/'));
            // JSON 문자열을 객체로 변환
            return JSON.parse(payload);
        } catch (e) {
            console.error('JWT 디코딩 실패:', e);
            return null;
        }
    };

    // 사용자 정보를 JWT에서 가져오는 함수
    const getUserFromToken = useCallback(() => {
        const { accessToken } = getAuthTokens();
        if (!accessToken) return null;

        const decodedToken = decodeJWT(accessToken);
        return decodedToken || {};
    }, [getAuthTokens]);

    return {
        loading,
        error,
        currentUserData,
        getCurrentUser,
        submitPendingApprovalInfo,
        getAuthTokens,
        user,
        getUserFromToken  // JWT에서 사용자 정보 가져오는 함수
    };

};

export default useAuth;